import React, { useMemo } from 'react';
import { BORDER, FONTS, TYPOGRAPHY } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import {
  DISPLAY_SIZE_TO_IMAGE_SIZE,
  OrderableItemListItemImage,
} from '@/components/orders/orderable-item-list-item-image';
import { ItemListOption } from '@/contexts/item-context-types';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import {
  useIsPhotoHidden,
  useItemDisplayOptions,
} from '@/contexts/new-order-context';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  listDisplaySize?: ListDisplaySize;
  option: ItemListOption;
  rightComponent?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

export const SubscriptionListItemContent: React.FC<Props> = ({
  containerStyle,
  option,
  rightComponent,
  listDisplaySize = ListDisplaySize.Medium,
  style,
}) => {
  const [styles] = useStyles();
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const { isLargeLayout } = useLayoutContext();
  const imageSize = DISPLAY_SIZE_TO_IMAGE_SIZE[listDisplaySize];
  const isPhotoHidden = useIsPhotoHidden(option.metadata!, listDisplaySize);
  const photoSizeStyle = {
    height: imageSize,
    width: imageSize,
  };

  const altText = [option.metadata?.displayName, option.metadata?.externalID]
    .filter(Boolean)
    .join('-');

  const description = useMemo(() => {
    const packUnit =
      option.metadata?.packDisplayString ?? option.metadata?.packUnit ?? '';
    const itemCode = option.metadata?.externalID ?? '';
    const separator = packUnit.length && itemCode.length ? ' • ' : '';
    return (
      <Text
        numberOfLines={option.metadata?.badges?.length ? 1 : 2}
        style={TYPOGRAPHY.description}
      >
        <Text style={styles.capitalizedText}>{packUnit}</Text>
        {separator}
        {itemCode}
      </Text>
    );
  }, [option.metadata, styles.capitalizedText]);

  return (
    <View style={[styles.base, containerStyle]}>
      {!isPhotoHidden ? (
        <View style={[styles.imageContainer, photoSizeStyle]}>
          <OrderableItemListItemImage
            alt={altText}
            hidePlaceholderImages={hidePlaceholderImages}
            imageURLs={option.metadata?.photoURLs || []}
            listDisplaySize={listDisplaySize}
          />
        </View>
      ) : null}
      <View style={[styles.textContainer, style]}>
        <Text
          numberOfLines={1}
          style={[
            isLargeLayout ? styles.titleMedium : styles.titleSmall,
            FONTS.primarySemiBold,
          ]}
        >
          {option.title}
        </Text>
        {description}
      </View>
      {rightComponent}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flexDirection: 'row',
  },
  capitalizedText: {
    textTransform: 'capitalize',
  },
  imageContainer: {
    ...BORDER.primary,
    alignItems: 'flex-end',
    marginRight: 8,
    overflow: 'hidden',
  },
  textContainer: {
    flex: 1,
  },
  titleMedium: {
    fontSize: theme.fontSizes.medium,
    lineHeight: 24,
    marginBottom: 4,
  },
  titleSmall: {
    fontSize: theme.fontSizes.small,
    lineHeight: 21,
    marginBottom: 4,
  },
}));
